import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { enablePush, disablePush, getPushStatus, isPushSupported } from "../lib/push";

/**
 * Botón compacto para activar / desactivar push notifications.
 * Props:
 *   - role: "customer" | "user"  (a qué endpoint suscribirse)
 *   - variant: shadcn variant
 *   - className
 *   - label: texto opcional (si no, muestra solo icono + estado corto)
 */
export function PushNotificationsToggle({ role = "user", variant = "outline", className = "", label }) {
  const [status, setStatus] = useState({ supported: false, permission: "default", subscribed: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await getPushStatus();
      if (!cancelled) setStatus(s);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!status.supported && !isPushSupported()) {
    return null;
  }

  const onClick = async () => {
    setLoading(true);
    try {
      if (status.subscribed) {
        await disablePush({ role });
        toast.success("Notificaciones desactivadas");
      } else {
        await enablePush({ role });
        toast.success("Notificaciones activadas. Te avisaremos al instante.");
      }
      const s = await getPushStatus();
      setStatus(s);
    } catch (e) {
      toast.error(e?.message || "No se pudo cambiar el estado de notificaciones");
    } finally {
      setLoading(false);
    }
  };

  const denied = status.permission === "denied";
  const Icon = denied ? BellOff : status.subscribed ? BellRing : Bell;
  const text =
    label !== undefined
      ? label
      : denied
      ? "Notificaciones bloqueadas"
      : status.subscribed
      ? "Notificaciones activas"
      : "Activar notificaciones";

  return (
    <Button
      type="button"
      variant={variant}
      size="sm"
      disabled={loading || denied}
      onClick={onClick}
      className={className}
      data-testid={`push-toggle-${role}-${status.subscribed ? "on" : "off"}`}
      title={denied ? "Habilita las notificaciones en los ajustes del navegador" : ""}
    >
      <Icon className="h-4 w-4 mr-2" />
      {text}
    </Button>
  );
}
